
import { reactive, toRefs } from "vue";
import VueFeather from "vue-feather";
import { BalenaDevice } from "@/interfaces/Balena";
import { InvalidCredentialsError, PermissionsError } from "@/errors/errors";
import { setBalenaAuthToken, clearBalenaAuthToken, getBalenaAuthToken } from "@/helpers/store";
import {
  postBalenaLogin,
  fetchBalenaDevices,
  fetchBalenaFleets,
  fetchBalenaAggregatorConfig,
  updateBalenaAggregatorConfig,
} from "@/helpers/api";

export default {
  name: "AggregatorsPage",
  components: {
    VueFeather,
  },
  setup() {
    const state = reactive({
      status: "",
      devices: [] as BalenaDevice[],
      selected_device: null as BalenaDevice | null,
      config_toml: "",
      original_config_toml: "",

      // Balena auth UI state
      balenaApiToken: "",
      balenaFleets: [] as string[],
      selectedFleet: "",
      balenaLoginError: "",
      balenaLoggedIn: getBalenaAuthToken() !== "",
    });

    const load_balena_fleets = async () => {
      try {
        state.balenaFleets = await fetchBalenaFleets();
        if (!state.selectedFleet && state.balenaFleets.length > 0) {
          state.selectedFleet = state.balenaFleets[0];
        }
      } catch {
        // keep quiet; user will see status/errors when trying to login
      }
    };

    const balenaLogin = async () => {
      state.balenaLoginError = "";
      state.status = "Logging into Balena...";
      try {
        const response = await postBalenaLogin(state.balenaApiToken, state.selectedFleet);
        setBalenaAuthToken(response.AuthToken);

        // clear password if successful
        state.balenaApiToken = "";
        state.balenaLoggedIn = true;
        state.status = "";
        await load_devices();
      } catch (error) {
        state.status = "";
        if (error instanceof InvalidCredentialsError) {
          state.balenaLoginError = error.message;
        } else if (error instanceof Error) {
          state.balenaLoginError = "Balena login failed.";
        }
      }
    };

    const balenaLogout = () => {
      clearBalenaAuthToken();
      state.balenaLoggedIn = false;
      state.devices = [];
      state.selected_device = null;
      state.config_toml = "";
      state.original_config_toml = "";
      state.status = "Balena token cleared. Please login to view devices.";
    };

    const load_config = async (device: BalenaDevice) => {
      state.selected_device = device;
      state.original_config_toml = state.config_toml = "loading...";
      state.config_toml = await fetchBalenaAggregatorConfig(device.UUID);
      state.original_config_toml = state.config_toml;
    };

    const load_devices = async () => {
      if (!state.balenaLoggedIn) {
        state.status = "Please login to Balena to load devices.";
        return;
      }

      state.status = "Loading devices...";
      let devices: BalenaDevice[] = [];
      try {
        devices = await fetchBalenaDevices();
      } catch (error) {
        if (error instanceof InvalidCredentialsError) {
          state.balenaLoggedIn = false;
          state.status = "Balena login required.";
          return;
        }
        if (error instanceof PermissionsError) {
          state.status = "You do not have the required 'device_admin' role permissions to access this page.";
          return;
        }
        state.status = "Error loading devices, the server is probably configured incorrectly.";
        return;
      }

      if (devices.length === 0) {
        state.devices = [];
        state.status = "There are no devices in this Balena fleet";
        return;
      }

      devices.sort((a, b) => a.Name.localeCompare(b.Name));
      state.devices = devices;
      state.status = "";
      await load_config(devices[0]);
    };

    const cancel_edit = () => {
      state.config_toml = state.original_config_toml;
    };

    const save_config = async () => {
      if (state.selected_device === null) return;
      try {
        await updateBalenaAggregatorConfig(state.selected_device.UUID, state.config_toml);
        await load_config(state.selected_device);
      } catch (error) {
        if (error instanceof InvalidCredentialsError) {
          state.balenaLoggedIn = false;
          state.status = "Balena login required.";
        } else {
          throw error;
        }
      }
    };
    // it is needed for the login as well
    load_balena_fleets();

    // Only auto-load if already have a Balena token saved
    if (state.balenaLoggedIn) {
      load_devices();
    } else {
      state.status = "Please login to Balena to load devices.";
    }

    return {
      ...toRefs(state),
      balenaLogin,
      balenaLogout,
      load_devices,
      load_config,
      save_config,
      cancel_edit,
    };
  },
  methods: {},
};
